import React, { useState, useEffect } from "react";
import { Button} from "./ui/button";
import { Card } from "./ui/card";
import backgroundImage from "../images/background1.webp"; 
import caldocsLogo from "../images/logo.png";
import calanceLogo from "../images/anchor.webp";
import Cookies from 'js-cookie';
import { useParams, useSearchParams } from "react-router-dom";

const backendUrl = import.meta.env.VITE_BACKEND_URL;
const platforms = ["hrms", "authorize"];
const platformMetaData = Object.fromEntries(
  platforms.map((platform) => [
    platform,
    {
      baseUrl: import.meta.env[`VITE_${platform.toUpperCase()}_BASE_API_URL`],
      welcomePath: import.meta.env[`VITE_${platform.toUpperCase()}_PLATFORM_PATH`],
      cookieKey: import.meta.env[`VITE_${platform.toUpperCase()}_COOKIE_KEY`],
    },
  ])
);

const Login = () => {
  const { platform } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [currentPlatform, setCurrentPlatform] = useState(null);

  useEffect(() => {
    if (platforms.includes(platform)) {
      setCurrentPlatform(platform);
    } else {
      setError("Invalid or Missing URL");
    }
  }, [platform]);
  
  useEffect(() => {
    try {
      if (!currentPlatform) return;
      if (currentPlatform === "authorize") return;
      const { cookieKey, baseUrl, welcomePath } = platformMetaData[currentPlatform];
      if (!cookieKey) return;
      const jwtToken = Cookies.get(cookieKey);
      if (jwtToken) {
        window.location.href = `${baseUrl}${welcomePath}`;
      }
    } catch (error) {
      console.error('[ERROR]:', error)
    }
  }, [currentPlatform]);
  
  const handleLogin = async () => {
    setLoading(true);
    setError("");

    try {
      if (platform === "authorize") {
        const params = new URLSearchParams();
        
        const response_type = searchParams.get('response_type');
        const client_id = searchParams.get('client_id');
        const redirect_uri = searchParams.get('redirect_uri');
        const scope = searchParams.get('scope');
        
        if (!response_type || !client_id || !redirect_uri || !scope) {
          setError("Missing required OAuth parameters");
          setLoading(false);
          return;
        }

        params.append('response_type', response_type);
        params.append('client_id', client_id);
        params.append('redirect_uri', redirect_uri);
        params.append('scope', scope);

        const state = searchParams.get('state');
        const nonce = searchParams.get('nonce');
        const code_challenge = searchParams.get('code_challenge');
        const code_challenge_method = searchParams.get('code_challenge_method');

        if (state) params.append('state', state);
        if (nonce) params.append('nonce', nonce);
        if (code_challenge) params.append('code_challenge', code_challenge);
        if (code_challenge_method) params.append('code_challenge_method', code_challenge_method);

        const authorizeUrl = `${backendUrl}/api/v2/authorize-main?${params.toString()}`;
        window.location.href = authorizeUrl;
        return;
      }

      const response = await fetch(backendUrl + "/auth/login" + `/${platform}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const redirectUrl = data?.data?.url;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          setError("Redirect URL not found.");
        }
      } else {
        setError("Failed to fetch the redirect URL.");
      }
    } catch (error) {
      setError("An error occurred during login.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex h-screen bg-cover"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <Card className="bg-slate-200 bg-opacity-90 p-2 rounded-none shadow-lg w-full max-w-[30%] ml-12">
        <div className="bg-slate-300 bg-opacity-70 mb-4 -m-2 shadow-lg">
          <div className="flex items-center mb-10">
            <img
              src={caldocsLogo}
              alt="CalDocs Logo"
              className="h-8 pl-2 pt-2 w-25 mr-2"
            />
          </div>
          <h2 className="text-center text-md font-800 pb-3">
            Select Authentication Provider
          </h2>
        </div>

        <Button
          onClick={handleLogin}
          disabled={loading}
          className="flex justify-start items-center w-full py-7 text-sm bg-white text-gray-500 font-semibold rounded-lg shadow-md transition hover:bg-gray-100"
        >
          <img src={calanceLogo} alt="Calance Logo" className="h-6 w-6 mr-3" />
          {loading ? "Logging in..." : "Calance AAD"}
        </Button>
        {error && <p className="text-red-500 mt-4 text-center">{error}</p>}
      </Card>
    </div>
  );
};

export default Login;
